import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Pagination } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import PageTitle from '@components/PageTitle'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Template from '@components/Template'
import Tr from '@components/Tr'
import { AiGenerationStatus } from '@models/aiGeneration.model'
import { aiGenerationApi } from '@services/AiGenerationApi'
import {
	formatGenerationCost,
	formatGenerationDate,
	formatGenerationDuration,
} from '@utils/helpers'
import { useFilters, useInputBounce, useQuery } from '@utils/hooks'

import GenerationStatusBadge from './StatusBadge'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
}

// Enquanto houver conversão andando, a lista se atualiza sozinha: quem acabou
// de enviar um caderno fica nesta tela, e recarregar à mão para saber se
// terminou é o tipo de trabalho que a tela existe para poupar.
const REFRESH_WHILE_RUNNING_MS = 10_000

const RUNNING: AiGenerationStatus[] = ['created', 'queued', 'processing']

function AiGenerations() {
	const pages = [{ name: 'Conversão de provas', href: '#', current: true }]
	const navigate = useNavigate()
	const query = useQuery()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))
	const [page, setPage] = useState(pageQuery)

	const { data, isLoading, refetch } = aiGenerationApi.useListQuery({
		page,
		search: searchQuery,
	})

	const hasRunning = Boolean(
		data?.data.some((generation) => RUNNING.includes(generation.status))
	)

	useEffect(() => {
		if (!hasRunning) {
			return undefined
		}

		const timer = setInterval(refetch, REFRESH_WHILE_RUNNING_MS)

		return () => clearInterval(timer)
	}, [hasRunning, refetch])

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/conversoes?page=1')
			setSearchQuery(value)
		},
		[navigate]
	)

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	useFilters({ search: searchQuery, fallbackUrl: '/conversoes' })

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title='Conversão de provas' />

			<Flex className={styles.searchContainer}>
				<SearchInput
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					className='flex-1'
					placeholder='Buscar pelo nome do arquivo'
				/>

				<Button onClick={() => navigate('/conversoes/nova')}>
					Nova conversão
				</Button>
			</Flex>

			{isLoading ? (
				<div className='flex justify-center'>
					<Spinner size='md' />
				</div>
			) : (
				<Table
					th={[
						{ label: 'arquivo' },
						{ label: 'prova' },
						{ label: 'questões' },
						{ label: 'status' },
						{ label: 'custo' },
						{ label: 'duração' },
						{ label: 'enviado em' },
					]}
				>
					{data?.data && data.data.length > 0 ? (
						data.data.map((generation) => (
							/* A linha inteira abre a conversão: abrir é a única coisa que
							   se faz com ela, e o botão só repetia isso num alvo menor. */
							<Tr
								key={generation.id}
								className='cursor-pointer hover:!bg-gray-100 transition-colors'
								onClick={() => navigate(`/conversoes/${generation.id}`)}
							>
								<Td className='px-6 py-4 align-top'>
									<span className='font-medium'>{generation.fileName}</span>
									{generation.admin?.name && (
										<span className='block text-sm text-gray'>
											{generation.admin.name}
										</span>
									)}
								</Td>

								<Td className='px-6 py-4 align-top'>
									{[
										generation.examBank,
										generation.examYear,
										generation.examPosition,
									]
										.filter(Boolean)
										.join(' · ') || '—'}
								</Td>

								<Td className='px-6 py-4 align-top'>
									{generation.receivedQuestions}
									{generation.totalQuestions > 0
										? ` de ${generation.totalQuestions}`
										: ''}
								</Td>

								<Td className='px-6 py-4 align-top'>
									<GenerationStatusBadge
										status={generation.status}
										error={generation.error}
									/>
								</Td>

								<Td className='px-6 py-4 align-top'>
									{formatGenerationCost(generation.costUsd)}
								</Td>

								<Td className='px-6 py-4 align-top'>
									{formatGenerationDuration(generation.durationMs)}
								</Td>

								<Td className='px-6 py-4'>
									{formatGenerationDate(generation.createdAt)}
								</Td>
							</Tr>
						))
					) : (
						<EmptyRow tdAmount={7} message='Nenhuma conversão encontrada' />
					)}
				</Table>
			)}

			<Divider className={styles.divider} />

			{data?.data && data.data.length > 0 && (
				<Pagination
					pageCount={data.meta.lastPage || 0}
					maxAmount={data.meta.total || 0}
					perPage={data.meta.perPage || 0}
				/>
			)}
		</Template>
	)
}

export default AiGenerations
